import * as yup from 'yup';
import { useFormik } from 'formik';
import { memo } from 'react';
import { DialogActions, Grid, useEventCallback, useMediaQuery, useTheme } from '@mui/material';
import { PmiButton } from 'shell/components/shared/button';
import PmiTextField from 'shell/components/shared/text-field';
import { PmiDialog } from 'shell/components/shared/dialog';
import { PmiDialogTitle } from 'shell/components/shared/dialog/title';
import { DialogContent } from '@mui/material';

export interface AuthorizationValueFormVM {
  value?: string;
  description?: string;
}

interface AuthorizationValueProps {
  onOk: (values: AuthorizationValueFormVM) => void;
  onCancel: () => void;
  isOpen: boolean;
}

const validationSchema = yup.object({
  value: yup.string().nullable().required('Il campo è obbligatorio'),
  description: yup.string().nullable().required('Il campo è obbligatorio'),
});

const AuthorizationValueComponent = ({
  onOk,
  onCancel,
  isOpen,
}: AuthorizationValueProps): React.ReactElement => {
  const onSubmit = useEventCallback(() => {
    onOk(authorizationValueForm.values);
    authorizationValueForm.resetForm();
  });

  const authorizationValueForm = useFormik<AuthorizationValueFormVM>({
    initialValues: {
      value: '',
      description: '',
    },
    validationSchema: validationSchema,
    onSubmit,
    enableReinitialize: true,
  });

  const theme = useTheme();
  const fullScreen = useMediaQuery(theme.breakpoints.down(theme.breakpoints.values.md));

  return (
    <PmiDialog
      open={isOpen}
      onClose={onCancel}
      fullScreen={fullScreen}
      aria-labelledby="authorizationValue-dialog-title"
      aria-describedby="authorizationValue-dialog-description"
    >
      <PmiDialogTitle id="authorizationValue-dialog-title" backgroundColor="primary" color="white">
        Aggiungi nuovo Valore
      </PmiDialogTitle>

      <DialogContent style={{ padding: '30px 24px' }}>
        <Grid container spacing={2} alignItems="center">
          <Grid item xs={12} md={4}>
            <PmiTextField
              label="Valore *"
              aria-label="Valore"
              id="value"
              name="value"
              fullWidth
              type="text"
              variant="outlined"
              value={authorizationValueForm.values.value}
              onChange={authorizationValueForm.handleChange}
              inputProps={{
                maxLength: '1',
              }}
              error={
                authorizationValueForm.touched.value && Boolean(authorizationValueForm.errors.value)
              }
              helperText={authorizationValueForm.errors.value}
            />
          </Grid>
          <Grid item xs={12} md={8}>
            <PmiTextField
              label="Descrizione *"
              aria-label="Descrizione"
              id="description"
              name="description"
              fullWidth
              type="text"
              variant="outlined"
              value={authorizationValueForm.values.description}
              onChange={authorizationValueForm.handleChange}
              inputProps={{
                maxLength: '50',
              }}
              error={
                authorizationValueForm.touched.description &&
                Boolean(authorizationValueForm.errors.description)
              }
              helperText={authorizationValueForm.errors.description}
            />
          </Grid>
        </Grid>
      </DialogContent>
      <DialogActions style={{ padding: '24px 24px 16px 24px' }}>
        <PmiButton
          variant="contained"
          color="primary"
          type="submit"
          onClick={() => {
            authorizationValueForm.submitForm();
          }}
        >
          Conferma
        </PmiButton>
        <PmiButton
          variant="outlined"
          color="cdmBrand"
          onClick={() => {
            onCancel();
          }}
        >
          Annulla
        </PmiButton>
      </DialogActions>
    </PmiDialog>
  );
};
AuthorizationValueComponent.displayName = 'Authorization Value component';

const AuthorizationValue = memo(AuthorizationValueComponent);

export default AuthorizationValue;
